import { GET_STUDIO_BY_SLUG, GET_STUDIOS } from '@graphql/queries/getStudios'
import useGoogleTracking from '@hooks/useGoogleTracking'
import useTagmanager from '@hooks/useTagmanager'
import { getStoryblokApi } from '@storyblok/react'
import { useRouter } from 'next/router'
import Script from 'next/script'
import type { GetStaticProps, InferGetStaticPropsType } from 'next/types'
import { StoryblokPage, transformToNested } from 'pages/sunpoint'
import React, { useEffect } from 'react'
import { ISbLinks } from 'storyblok-js-client'

import Col from '@components/atoms/Grid/Col'
import Row from '@components/atoms/Grid/Row'
import Headline from '@components/atoms/Headline'
import { HeadlineImportances } from '@components/atoms/Headline/Headline'
import {
  BackupMetaSunpoint,
  MetaTags,
} from '@components/molecules/MetaTags/MetaTags'
import StudioDetailPage from '@components/pages/StudioDetailPage'
import SunpointTemplateDefault from '@components/templates/SunpointTemplateDefault'

import RealEnvironmentContext from '@helper/context/RealEnviromentContext'
import { useUserData } from '@helper/context/UserDataContext'
import { prepareStudios } from '@helper/prepareStudios'

import { RoutesSunpoint } from '@definitions/routes/RoutesSunpoint'
import { Studio } from '@definitions/types/symfonyTypesd'

import apolloClient from '../../../../apollo-client'

interface StudioIndexProps {
  menuData: ISbLinks
  studio?: Studio
}

export default function StudioIndex({
  menuData,
  studio,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const { userData, setUserData } = useUserData()
  const { trackPageView } = useGoogleTracking()
  const router = useRouter()
  useTagmanager()

  useEffect(() => {
    trackPageView(router.asPath, studio?.title ?? 'Studios - SUNPOINT GmbH')
  }, [router.asPath, trackPageView])

  return (
    <SunpointTemplateDefault menuData={transformToNested(menuData)}>
      {studio !== undefined && (
        <MetaTags
          _uid={studio?.id}
          title={studio?.title + ' | SUNPOINT GmbH'}
          description={studio?.seoDescription}
          og_description={studio?.seoDescription}
          og_title={studio?.title + ' | SUNPOINT GmbH'}
          og_image={'/img/og_1200_630.jpg'}
          twitter_image={'/img/og_1200_630.jpg'}
          twitter_title={studio?.title + ' | SUNPOINT GmbH'}
          twitter_description={studio?.seoDescription}
        />
      )}
      {studio === undefined && <BackupMetaSunpoint />}
      <Script
        data-settings-id={process.env.userCentricsId ?? ''}
        id="usercentrics-cmp"
        src="https://app.usercentrics.eu/browser-ui/latest/loader.js"
      ></Script>
      <RealEnvironmentContext.Provider value={{ isRealEnvironment: true }}>
        <div>
          {studio === undefined && (
            <Headline importance={HeadlineImportances.h1}>
              Kein Studio gefunden
            </Headline>
          )}
          {studio && (
            <StudioDetailPage
              studio={studio}
              handleSelectContract={(contract) => {
                setUserData({
                  studio: studio,
                  contract: contract,
                  formData: userData.formData,
                })
                router.push(RoutesSunpoint.SUNPOINT__CLUBMITGLIEDWERDEN)
              }}
            />
          )}
        </div>
      </RealEnvironmentContext.Provider>
    </SunpointTemplateDefault>
  )
}

export const getStaticProps: GetStaticProps<StudioIndexProps> = async (
  context: any
) => {
  const { slug } = context.params as { slug: string[] }
  const storyblokApi = getStoryblokApi()
  let studio = null
  let links

  try {
    let { data: linkData } = await storyblokApi.get('cdn/links', {
      version: 'draft',
      per_page: 5000,
      starts_with: 'sunpoint',
      is_startpage: true,
      with_tag: 'InHeaderNavigation',
    })

    if (linkData) {
      links = linkData.links
    }
  } catch (error) {
    console.error('Fehler beim Abrufen der Links:', error)
  }

  try {
    const slugString = slug.join('/')
    const { data } = await apolloClient.query({
      query: GET_STUDIO_BY_SLUG,
      variables: {
        slug: slugString,
      },
      fetchPolicy: 'no-cache',
    })
    studio = data.studios.edges[0].node
  } catch (error) {
    console.error('Fehler beim Abrufen der Studios:', error)
  }

  return {
    props: {
      menuData: links,
      studio: {
        ...studio,
      },
    },
    revalidate: 20,
  }
}

export const getStaticPaths: any = async () => {
  let studios: Studio[] = []

  try {
    const { data } = await apolloClient.query({
      query: GET_STUDIOS,
    })

    studios = data.studios
  } catch (error) {
    console.error('Fehler beim Abrufen der Studios:', error)
  }

  const preparedStudios = prepareStudios(studios)
  const paths = preparedStudios.map((studio: Studio) => {
    const splittedSlug = studio.slug
      .split('/')
      .filter((el: string) => el.length != 0)
    return {
      params: { slug: splittedSlug, locale: 'de' },
    }
  })

  return {
    paths,
    fallback: 'blocking',
  }
}
